import { useEffect, useRef } from "react";
import { Paperclip, Star } from "lucide-react";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { formatTaipeiDateTime } from "../../lib/datetime";
import type { MailMessage } from "../../api/types";

/** 列表的一列:一封信,或(對話串模式)一串的最新一封 + 封數與參與者。 */
export type MailListRow = MailMessage & {
  thread_key?: string;
  count?: number;
  unread?: number;
  participants?: string[];
};

// 信件列表(中欄)。讀的是**系統備份**:一次 50 封、新的在上,往下捲載更多。
//
// - 雲端已刪除的信只在「系統備份」裡列出,灰字 + 「雲端已刪除」小標(一般資料夾不列)。
// - 未讀、星號、附件都帶文字(徽章或螢幕閱讀器文字),不只靠粗體 / 顏色 / 圖示。
// - 每一列是一顆按鈕,Tab 走得到、Enter 打得開。
// - §2.6:逐封模式每列前面有勾選框(批次動作);對話串模式一串一列,寫「N 封」與參與者。
//   快捷鍵 j/k 走到的那一列標 `data-cursor`(外框),Enter 打開它。

export interface MailListProps {
  items: MailListRow[];
  selectedId: number | null;
  /** 快捷鍵 j/k 走到的那一列。 */
  cursorId?: number | null;
  /** 勾選框(逐封模式才有)。沒給 = 不畫勾選框。 */
  checked?: Set<number>;
  onToggleCheck?: (id: number) => void;
  onToggleAll?: () => void;
  /** 寄件備份裡看的是「寄給誰」,不是寄件人(都是自己)。 */
  showRecipients: boolean;
  loading: boolean;
  hasMore: boolean;
  emptyText: string;
  onOpen: (item: MailListRow) => void;
  onLoadMore: () => void;
}

export function MailList({
  items,
  selectedId,
  cursorId = null,
  checked,
  onToggleCheck,
  onToggleAll,
  showRecipients,
  loading,
  hasMore,
  emptyText,
  onOpen,
  onLoadMore,
}: MailListProps) {
  // 捲到底自動載下一頁。jsdom / 舊瀏覽器沒有 IntersectionObserver —— 那時靠「載入更多」那顆鈕。
  const sentinel = useRef<HTMLDivElement | null>(null);
  const loadMore = useRef(onLoadMore);
  loadMore.current = onLoadMore;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || typeof window.IntersectionObserver === "undefined") return;
    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore.current();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, items.length]);

  if (!loading && items.length === 0) {
    return <p className="m-0 p-2 text-sm text-[var(--text-3)]">{emptyText}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {checked && onToggleAll && items.length > 0 ? (
        <label className="inline-flex items-center gap-2 px-1 text-xs text-[var(--text-2)]">
          <input
            type="checkbox"
            checked={items.every((one) => checked.has(one.id))}
            onChange={onToggleAll}
            className="h-4 w-4 accent-[var(--accent)]"
          />
          全選
        </label>
      ) : null}
      <ul aria-label="信件清單" className="m-0 flex flex-col gap-1 p-0">
        {items.map((one) => {
          const thread = typeof one.count === "number";
          const who = thread && one.participants && one.participants.length > 0
            ? one.participants.join("、")
            : showRecipients
              ? `給 ${one.to_addrs.join("、") || "(沒有收件人)"}`
              : one.from_name || one.from_addr || "(未知寄件人)";
          const gone = one.deleted_on_server;
          const unread = thread ? (one.unread ?? 0) > 0 : !one.seen && !gone;
          const isCursor = cursorId === one.id;
          return (
            <li key={one.thread_key ?? one.id} className="flex list-none items-stretch gap-1.5">
              {checked && onToggleCheck ? (
                <input
                  type="checkbox"
                  aria-label={`選取:${one.subject || "(無主旨)"}`}
                  checked={checked.has(one.id)}
                  onChange={() => onToggleCheck(one.id)}
                  className="mt-3 h-4 w-4 shrink-0 accent-[var(--accent)]"
                />
              ) : null}
              <button
                type="button"
                data-mail-row={one.id}
                data-cursor={isCursor ? "true" : undefined}
                aria-current={selectedId === one.id ? "true" : undefined}
                onClick={() => onOpen(one)}
                className={
                  "flex min-w-0 flex-1 flex-col gap-0.5 rounded-[var(--radius)] border px-3 py-2 text-left " +
                  (selectedId === one.id
                    ? "border-[var(--accent)] bg-[var(--accent-soft)] "
                    : "border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-2)] ") +
                  (isCursor ? "ring-2 ring-[var(--accent)] " : "")
                }
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-sm " +
                      (gone ? "text-[var(--text-3)]" : unread ? "font-semibold text-[var(--text)]" : "text-[var(--text)]")
                    }
                  >
                    {who}
                  </span>
                  <span className="shrink-0 text-xs tabular text-[var(--text-3)]">{formatTaipeiDateTime(one.date)}</span>
                </span>
                <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {thread && (one.count ?? 0) > 1 ? <Badge tone="neutral">{one.count} 封</Badge> : null}
                  {unread ? <Badge tone="accent">未讀</Badge> : null}
                  {gone ? <Badge tone="neutral">雲端已刪除</Badge> : null}
                  {one.flagged ? (
                    <span className="inline-flex text-[var(--warn)]">
                      <Star aria-hidden="true" size={14} fill="currentColor" />
                      <span className="sr-only">已加星號</span>
                    </span>
                  ) : null}
                  {one.has_attachments ? (
                    <span className="inline-flex text-[var(--text-3)]">
                      <Paperclip aria-hidden="true" size={14} />
                      <span className="sr-only">有附件</span>
                    </span>
                  ) : null}
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-sm " + (gone ? "text-[var(--text-3)]" : "text-[var(--text-2)]")
                    }
                  >
                    {one.subject || "(無主旨)"}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {loading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {hasMore && !loading ? (
        <div ref={sentinel}>
          <Button size="sm" variant="ghost" onClick={onLoadMore}>
            載入更多
          </Button>
        </div>
      ) : null}
    </div>
  );
}
